/**
 * Plan limit UI helpers: shared across taskpane and custom function error paths.
 * All call sites must go through classifyError—never reach into proxy shapes directly.
 */

import {
  classifyError,
  normalizeToPlanLimitInfo as normalizeFromError,
  type PlanLimitInfo,
} from "../errors/errorKinds";

export type { PlanLimitInfo } from "../errors/errorKinds";

/** True when error is a plan limit (uses classifyError). */
export function isPlanLimitError(err: unknown, status?: number): boolean {
  const result = classifyError(err, status);
  return result.kind === "plan_limit";
}

/**
 * Produce PlanLimitInfo from error. Use when classifyError returned kind "plan_limit".
 * Falls back to normalizeToPlanLimitInfo if result.info is missing.
 */
export function getPlanLimitInfo(
  err: unknown,
  status?: number,
  source?: PlanLimitInfo["source"]
): PlanLimitInfo {
  const result = classifyError(err, status);
  if (result.kind === "plan_limit" && result.info) {
    return source ? { ...result.info, source } : result.info;
  }
  return normalizeFromError(err, status, source);
}

export { normalizeToPlanLimitInfo } from "../errors/errorKinds";

/**
 * Format short message for in-cell errors. Keep under ~80 chars for Excel display.
 * Example: "Free plan limit (max 10 chain rows). Upgrade to Pro. Request: abc123"
 */
export function formatPlanLimitMessage(info: PlanLimitInfo): string {
  const limitPart =
    info.limit_key && info.limit_value != null
      ? " (max " +
        String(info.limit_value) +
        " " +
        (info.limit_key === "max_chain_rows"
          ? "chain rows"
          : info.limit_key === "max_refresh_items_per_run"
            ? "items"
            : "requests") +
        ")"
      : "";
  const reqPart = info.request_id ? " Request: " + info.request_id.substring(0, 8) : "";
  const sep = limitPart ? limitPart + " " : " ";
  return "Free plan limit reached" + sep + "Upgrade to Pro." + reqPart;
}
